import React, { useContext, useCallback, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Handle, Position } from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Icons from '../Icons';
import AuthContext from '../../auth/Context';   // toàn cục — chỉnh lại nếu cấu trúc khác
import FlowContext from '../Context';            // FlowEditor's Context — apiUrl, flowInstanceId
import UserformTaskPanel from './UserformTaskPanel';
import { useQuickAction } from '../../DataQuickActionContext'; 

import { useModalManager } from '../../ModalManagerContext'; 

// ── Helper ────────────────────────────────────────────────────────────────────
const isNodeDataComplete = (data, metadata) => {
    if (!metadata) return true;
    for (const field of metadata) {
        if (field.required && field.default === undefined &&
            (data[field.name] === undefined || data[field.name] === null || data[field.name] === '')) {
            return false;
        }
    }
    return true;
};

// ── TaskPanelPortal ───────────────────────────────────────────────────────────
const TaskPanelPortal = ({ nodeId, anchorEl, onClose, onCountChange }) => {

    const modalManager = useModalManager();
    const requestZIndex = modalManager?.requestZIndex;
    const releaseZIndex = modalManager?.releaseZIndex;
    const zIndex = requestZIndex();

    const [pos, setPos] = useState({ top: 0, left: 0 });
    const panelRef = useRef(null);

    useEffect(() => {
        if (!anchorEl) return;
        const rect   = anchorEl.getBoundingClientRect();
        const panelW = 320;
        const left   = rect.right + 8 + panelW < window.innerWidth
            ? rect.right + 8
            : rect.left - panelW - 8;
        setPos({ top: Math.max(8, rect.top), left });
    }, [anchorEl]);

    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target) &&
                anchorEl && !anchorEl.contains(e.target)) {
                releaseZIndex(zIndex);
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [anchorEl, onClose]);

    return ReactDOM.createPortal(
        <div
            ref={panelRef}
            style={{
                position: 'fixed', top: pos.top, left: pos.left,
                width: 320, maxHeight: 440, zIndex: zIndex,
                background: '#fff', borderRadius: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                border: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
        >
            <div style={{
                padding: '8px 12px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: 6, background: '#f9fafb',
            }}>
                <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>📋 Chứng từ cần xử lý</span>
                <button onClick={onClose} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: '#6b7280', lineHeight: 1,
                }}>×</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
                <UserformTaskPanel
                    filterNodeId={nodeId}
                    onTaskCountChange={onCountChange}
                    pollInterval={30000}
                />
            </div>
        </div>,
        document.body
    );
};

// ── Component chính ───────────────────────────────────────────────────────────
const UserformNode = ({nodeName, data, id, selected, metadata, type, style = {} }) => {
    //console.log("[userformNode]",data);
    const authContext = useContext(AuthContext);  // quickAdd, apis
    const {openAdd} = useQuickAction();
    const flowContext  = useContext(FlowContext);  // apiUrl, flowInstanceId
    const isRunning              = !!flowContext?.isRunning;

    const nodeRef      = useRef(null);

    const [pendingCount,  setPendingCount]  = useState(0);
    const [panelOpen,     setPanelOpen]     = useState(false);

    // ── Trạng thái từ flow engine ───────────────────────────────────────────
    const nodeStatus  = data?.nodeState?.status || '';
    const isPending   = nodeStatus === 'pending_input';
    const isListening = nodeStatus === 'active_listening';
    const isCompleted = nodeStatus === 'completed';
    const isFailed    = nodeStatus === 'failed';

    // node.data.code: form sẽ mở khi xử lý task (cấu hình tĩnh trên node)
    const nodeCode    = data?.code;
    
    // node.data.listen_code: chứng từ nguồn để lắng nghe sự kiện (có thể khác nodeCode)
    const listenCode  = data?.listen_code;
    const isComplete  = isNodeDataComplete(data, metadata);

    // Secret cho webhook "Tạo mới" — do FlowEngine sinh ra khi flow start,
    // expose qua getFlowStatus() (FlowEngine_patches.js PATCH 7). Tồn tại
    // suốt vòng đời instance, không đổi theo từng lần task được tạo.
    const directCompleteSecret = data?.nodeState?.directCompleteSecret;
    const flowInstanceId       = flowContext?.flowInstanceId;
    const apiUrl                = flowContext?.apiUrl;

    let statusClass = '';
    if (data?.disabled)  statusClass = 'node-disabled';
    else if (isPending)  statusClass = 'node-status-waiting-input';
    else if (nodeStatus) statusClass = `node-status-${nodeStatus}`;

    // ── Polling số task đang chờ tại node này ──────────────────────────────
    const fetchCount = useCallback(async () => {
        if(!isRunning) return;
        try {
            const token = authContext?.userInfo?.token;
            if (!token) return;
            const res  = await authContext.apis.asyncGetList(token, 'flowpending', {
                condition: { node_id: id, status: 'pending' }
            });
            const list = Array.isArray(res) ? res : (res?.data || []);
            setPendingCount(list.length);
        } catch (e) {}
    }, [isRunning,id, authContext]);

    useEffect(() => {
        if(!isRunning) return;
        fetchCount();
        const iv = setInterval(fetchCount, 30000);
        return () => clearInterval(iv);
    }, [fetchCount,isRunning]);

    // ── Nút "Tạo mới" — qua webhook, không tạo flowpending từ client ──────
    const handleCreate = useCallback(async (e) => {
        e.stopPropagation();
        if (!nodeCode || !isRunning) return;
        const onSuccess = async (savedDoc) => {
            console.log("[userformNode] Đã tạo thành công",savedDoc);
            fetchCount();
            if (!flowInstanceId || !apiUrl) {
                console.warn('[UserformNode] Không xác định được flowInstanceId/apiUrl — '
                    + 'flow có thể chưa chạy, chứng từ đã lưu nhưng flow sẽ không tự tiếp tục.');
                return;
            }
            try {
                console.log("[userformNode] Gọi webhooks báo cho flow biết...");
                const url = `${apiUrl}/webhooks/${flowInstanceId}/userform-complete/${id}`;
                const res = await fetch(url, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({
                        __direct_complete: true,
                        __secret:          directCompleteSecret,
                        result:            savedDoc,
                    }),
                });
                if (!res.ok) {
                    console.warn('[UserformNode] Webhook trả về lỗi:', res.status);
                }
            } catch (err) {
                console.error('[UserformNode] Không gọi được webhook tiếp tục flow:', err);
            }
        };

        //authContext.quickAdd(nodeCode, {}, onSuccess);
        openAdd(nodeCode,null,"Thêm mới",{},onSuccess);
    }, [nodeCode, id, flowInstanceId, apiUrl, directCompleteSecret, authContext,isRunning, fetchCount]);

    // ── Toggle panel danh sách task ────────────────────────────────────────
    const handleTogglePanel = useCallback((e) => {
        e.stopPropagation();
        if (!isRunning) return;
        setPanelOpen(v => !v);
    }, [isRunning]);
    const handleClosePanel = useCallback(() => setPanelOpen(false), []);
    const handleCountChange = useCallback((n) => {
        setPendingCount(n);
        if (n === 0) setPanelOpen(false);
    }, []);

    // ── Nhãn trạng thái ───────────────────────────────────────────────────
    const statusLabel = isPending   ? '⚡ Chờ nhập liệu'
        : isListening ? '👂 Đang lắng nghe'
        : isCompleted ? '✅ Hoàn thành'
        : isFailed    ? '❌ Có lỗi'
        : nodeStatus  || null;

    const accentColor = isPending ? '#6c63ff' : isListening ? '#0ea5e9' : '#ccc';

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div
                ref={nodeRef}
                style={{
                    border: `2px solid ${accentColor}`,
                    padding: 10, minWidth: 148, position: 'relative',
                    ...style,
                }}
                className={`base-node userform-node ${statusClass} ${selected ? 'selected-node' : ''}`}
            >
                <Handle type="target" position={Position.Left} isConnectable={true} />

                {!isComplete && <div className="node-warning">⚠️</div>}

                {/* Badge số task chờ */}
                {pendingCount > 0 && (
                    <div
                        onClick={handleTogglePanel}
                        title={`${pendingCount} chứng từ chờ xử lý`}
                        style={{
                            position: 'absolute', top: -9, right: -9,
                            background: panelOpen ? '#b91c1c' : '#ef4444', color: '#fff',
                            borderRadius: '50%', minWidth: 18, height: 18,
                            fontSize: 10, fontWeight: 700, padding: '0 3px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                            cursor: 'pointer', zIndex: 10,
                            transform: panelOpen ? 'scale(1.15)' : 'scale(1)',
                            transition: 'transform .1s',
                        }}
                    >
                        {pendingCount > 99 ? '99+' : pendingCount}
                    </div>
                )}

                {/* Dot nhấp nháy */}
                {(isPending || isListening) && (
                    <div style={{
                        position: 'absolute', top: 6, left: 6,
                        width: 7, height: 7, borderRadius: '50%',
                        background: accentColor, animation: 'pulse 1.5s infinite',
                    }} />
                )}

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FontAwesomeIcon
                        icon={Icons[type] || Icons.default}
                        style={{ color: style.color || '#6c63ff', fontSize: 13 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: 12, fontWeight: 600, textAlign: 'left',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {data?.form_title || nodeName || data?.name || 'Nhập liệu'}
                        </div>
                        {(listenCode || nodeCode) && (
                            <div style={{ fontSize: 10, opacity: 0.65, marginTop: 2, lineHeight: 1.4 }}>
                                {listenCode && (
                                    <span title="Lắng nghe sự kiện của loại chứng từ này">👂 {listenCode}</span>
                                )}
                                {listenCode && nodeCode && (
                                    <span style={{ opacity: 0.5, margin: '0 3px' }}>→</span>
                                )}
                                {nodeCode && (
                                    <span title="Form sẽ mở khi xử lý task">📄 {nodeCode}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Trạng thái */}
                {statusLabel && (
                    <div style={{
                        fontSize: 10, marginTop: 5, padding: '2px 5px',
                        borderRadius: 3, textAlign: 'center', fontWeight: 500,
                        background: isPending   ? 'rgba(108,99,255,.1)'
                                  : isListening ? 'rgba(14,165,233,.1)'
                                  : isCompleted ? 'rgba(34,197,94,.1)'
                                  : isFailed    ? 'rgba(239,68,68,.1)'
                                  : 'rgba(0,0,0,.05)',
                        color: isPending   ? '#6c63ff'
                             : isListening ? '#0ea5e9'
                             : isCompleted ? '#16a34a'
                             : isFailed    ? '#dc2626'
                             : '#555',
                    }}>
                        {statusLabel}
                    </div>
                )}

                {/* Nút xem danh sách task */}
                {pendingCount > 0 && (
                    <button
                        onClick={handleTogglePanel}
                        style={{
                            marginTop: 6, width: '100%', padding: '4px 8px',
                            background: panelOpen ? '#1d4ed8' : '#2563eb', color: '#fff',
                            border: 'none', borderRadius: 4, cursor: 'pointer',
                            fontSize: 11, fontWeight: 600,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                        }}
                    >
                        📋 {pendingCount} chứng từ chờ xử lý
                    </button>
                )}

                {/* Nút tạo mới — chỉ hiện khi node.data.code có giá trị */}
                {nodeCode && (
                    <button
                        onClick={handleCreate}
                        disabled={!isRunning}
                        title={
                            !isRunning
                                ? 'Flow chưa chạy — cần Bắt đầu flow trước khi tạo mới'
                                : `Tạo mới chứng từ loại '${nodeCode}' và tiếp tục flow`
                        }
                        style={{
                            marginTop: 6, width: '100%', padding: '4px 8px',
                            background: 'rgba(0,0,0,.08)',
                            color: isRunning ? 'rgba(0,0,0,.55)' : 'rgba(0,0,0,.3)',
                            border: `1px dashed rgba(0,0,0,${isRunning ? 0.2 : 0.12})`,
                            borderRadius: 4,
                            cursor: !isRunning ? 'not-allowed' : 'pointer',
                            opacity: isRunning ? 1 : 0.6,
                            fontSize: 11, fontWeight: 600,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                        }}
                    >
                        ➕ Tạo mới
                    </button>
                )}

                <Handle type="source" position={Position.Right} id="a" isConnectable={true} />
            </div>

            {panelOpen && (
                <TaskPanelPortal
                    nodeId={id}
                    anchorEl={nodeRef.current}
                    onClose={handleClosePanel}
                    onCountChange={handleCountChange}
                />
            )}
        </>
    );
};

export default UserformNode;